/**
 * ExportButton — "Export CSV", the complete finding list (FR-017, FR-026,
 * C-005; ANA.12; style guide §7.1; mission 6 WP03 T023; research §1.14).
 *
 * Calls `exportFindings`, which reads the body whole through fetch and saves
 * it only when it arrived complete. State holds the phase and a byte count
 * (and, on a refusal, the problem to render); never a URL or the body. Busy
 * sets `aria-busy` and shows the bytes read so far in mono. A 503 `try-again`
 * renders its `detail` verbatim; a transfer that did not complete renders its
 * register sentence (register 12 for a failed read); any other problem renders
 * through `ProblemBand`. Unmounting aborts the read.
 */
import { useEffect, useRef, useState } from 'react'
import { Download } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ApiError, type Problem } from '@/lib/api'
import { TransferError, exportFindings } from '@/lib/files'
import { Mono, bytes as formatBytes } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'

import { Band } from './Band'
import { ProblemBand } from './ProblemBand'

type Phase =
  | { kind: 'idle' }
  | { kind: 'busy'; bytes: number }
  | { kind: 'saved'; filename: string; bytes: number }
  | { kind: 'busy-server'; detail: string }
  | { kind: 'failed'; sentence: string }
  | { kind: 'problem'; problem: Problem }

const TRY_AGAIN = 'https://trellis.bloomcredit.io/problems/try-again'

function phaseFor(error: unknown): Phase {
  if (error instanceof ApiError) {
    const p = error.problem
    if (p.type === TRY_AGAIN && p.status === 503 && p.detail) return { kind: 'busy-server', detail: p.detail }
    return { kind: 'problem', problem: p }
  }
  if (error instanceof TransferError) return { kind: 'failed', sentence: error.message }
  return { kind: 'failed', sentence: copyText('genericError') }
}

export function ExportButton({ runId }: { runId: string }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const abort = useRef<AbortController | null>(null)

  useEffect(() => () => abort.current?.abort(), [])

  async function run() {
    const controller = new AbortController()
    abort.current = controller
    setPhase({ kind: 'busy', bytes: 0 })
    try {
      const saved = await exportFindings(runId, {
        signal: controller.signal,
        onBytes: (total) => setPhase({ kind: 'busy', bytes: total }),
      })
      setPhase({ kind: 'saved', filename: saved.filename, bytes: saved.bytes })
    } catch (error) {
      if (!controller.signal.aborted) setPhase(phaseFor(error))
    }
  }

  const busy = phase.kind === 'busy'
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="flex items-center gap-3">
        <Button variant="secondary" loading={busy} icon={<Download aria-hidden />} onClick={() => void run()}>
          Export CSV
        </Button>
        {busy ? <Mono className="text-caption text-navy-600">{formatBytes(phase.bytes)}</Mono> : null}
        {phase.kind === 'saved' ? (
          <span className="text-caption text-navy-600">
            <Mono>{phase.filename}</Mono> · <Mono>{formatBytes(phase.bytes)}</Mono>
          </span>
        ) : null}
      </div>
      {phase.kind === 'busy-server' ? <Band kind="info">{phase.detail}</Band> : null}
      {phase.kind === 'failed' ? <Band kind="blocked">{phase.sentence}</Band> : null}
      {phase.kind === 'problem' ? <ProblemBand problem={phase.problem} /> : null}
    </div>
  )
}
